import { useEffect, useMemo, useState } from "react";
import { Table, Tag, Spin, Button, Space, Modal, InputNumber, Switch, message } from "antd";
import { EyeOutlined, SplitCellsOutlined } from "@ant-design/icons";
import Header from "../_shared/components/Header";
import { useGetJobExceptionsQuery, useSplitJobMutation } from "../../../redux/api/scheduling";
import { useShowThumbnails } from "../_shared/utils/useShowThumbnails";
import JobDetail from "../Jobs/JobDetail";
import {
  HIDDEN_TYPES_STORAGE_KEY,
  countByType,
  formatType,
  groupVisibleByJob,
  parseHiddenTypes,
  type JobGroup,
} from "./filterExceptions";

const TYPE_COLORS: Record<string, string> = {
  missing_task: "purple",
  late_risk: "orange",
  at_risk_completion: "gold",
  late_completion: "red",
  capacity_issue: "volcano",
  no_machine_capacity: "magenta",
  no_staff_capacity: "cyan",
  no_slot_available: "geekblue",
  missing_deadline: "lime",
  dependency_cycle: "red",
  cp_infeasible: "red",
  undefined: "default",
};

function useHiddenTypes(): [string[], (next: string[]) => void] {
  const [hidden, setHidden] = useState<string[]>(() => {
    try {
      return parseHiddenTypes(window.localStorage.getItem(HIDDEN_TYPES_STORAGE_KEY));
    } catch {
      return parseHiddenTypes(null);
    }
  });

  const update = (next: string[]) => {
    setHidden(next);
    try {
      window.localStorage.setItem(HIDDEN_TYPES_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // ignore — non-critical
    }
  };

  return [hidden, update];
}

export default function Exceptions() {
  const { data, isLoading } = useGetJobExceptionsQuery({});
  const [splitJob, { isLoading: isSplitting }] = useSplitJobMutation();
  const exceptions = data?.results || data || [];
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  // Split modal state
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [splitTarget, setSplitTarget] = useState<JobGroup | null>(null);
  const [splitCount, setSplitCount] = useState(2);
  const [showThumbnails, setShowThumbnails] = useShowThumbnails();

  const [hiddenTypes, setHiddenTypes] = useHiddenTypes();
  const [page, setPage] = useState(1);

  const typeCounts = useMemo(() => countByType(exceptions), [exceptions]);
  const presentTypes = useMemo(() => Object.keys(typeCounts).sort(), [typeCounts]);
  const jobGroups = useMemo(
    () => groupVisibleByJob(exceptions, hiddenTypes),
    [exceptions, hiddenTypes]
  );
  const hiddenPresent = presentTypes.filter((t) => hiddenTypes.includes(t));
  const hiddenCount = hiddenPresent.reduce((sum, t) => sum + typeCounts[t], 0);

  useEffect(() => {
    setPage(1);
  }, [hiddenTypes]);

  const toggleType = (type: string, visible: boolean) => {
    setHiddenTypes(
      visible ? hiddenTypes.filter((t) => t !== type) : [...hiddenTypes, type]
    );
  };

  const openSplitModal = (record: JobGroup, e: React.MouseEvent) => {
    e.stopPropagation();
    setSplitTarget(record);
    setSplitCount(2);
    setSplitModalOpen(true);
  };

  const handleSplit = async () => {
    if (!splitTarget) return;
    try {
      const result = await splitJob({ id: splitTarget.jobId, split_count: splitCount }).unwrap();
      message.success(
        `Split ${splitTarget.job_order_name} into ${result.length} jobs`
      );
      setSplitModalOpen(false);
      setSplitTarget(null);
    } catch {
      message.error("Failed to split job");
    }
  };

  if (selectedJobId) {
    return (
      <div className="w-full p-4">
        <Button onClick={() => setSelectedJobId(null)} className="mb-4">
          &larr; Back to Exceptions
        </Button>
        <JobDetail jobId={selectedJobId} />
      </div>
    );
  }

  const thumbnailColumn = {
    title: "",
    key: "thumbnail",
    width: 64,
    render: (_: any, record: JobGroup) =>
      record.job_thumbnail_file ? (
        <img className="h-12" src={record.job_thumbnail_file} alt="part" />
      ) : null,
  };

  const baseColumns = [
    { title: "Order", dataIndex: "job_order_name", key: "job_order_name", sorter: (a: JobGroup, b: JobGroup) => a.job_order_name.localeCompare(b.job_order_name) },
    { title: "Part", dataIndex: "job_part_name", key: "job_part_name" },
    {
      title: "Exception Types",
      key: "types",
      render: (_: any, record: JobGroup) => (
        <Space size={[4, 4]} wrap>
          {record.types.map((t) => (
            <Tag key={t} color={TYPE_COLORS[t] || "default"}>
              {formatType(t)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Summary",
      key: "summary",
      render: (_: any, record: JobGroup) => record.summary,
    },
    {
      title: "",
      key: "action",
      width: 160,
      render: (_: any, record: JobGroup) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => setSelectedJobId(record.jobId)}
          >
            Details
          </Button>
          <Button
            type="link"
            size="small"
            icon={<SplitCellsOutlined />}
            onClick={(e) => openSplitModal(record, e)}
          >
            Split
          </Button>
        </Space>
      ),
    },
  ];

  const columns = showThumbnails ? [thumbnailColumn, ...baseColumns] : baseColumns;

  return (
    <div className="w-full p-4">
      <Header title="Job Exceptions" />
      <div style={{ marginBottom: 12, display: "flex", gap: 12, alignItems: "center" }}>
        <Space size="small">
          <Switch checked={showThumbnails} onChange={setShowThumbnails} size="small" />
          <span>Show thumbnails</span>
        </Space>
      </div>
      {presentTypes.length > 0 && (
        <div style={{ marginBottom: 12 }}>
          <Space size={[4, 8]} wrap>
            <span style={{ color: "#888", marginRight: 4 }}>Show types:</span>
            {presentTypes.map((t) => {
              const visible = !hiddenTypes.includes(t);
              return (
                <Tag.CheckableTag
                  key={t}
                  checked={visible}
                  onChange={(checked) => toggleType(t, checked)}
                  style={{
                    border: `1px solid ${visible ? "transparent" : "#d9d9d9"}`,
                    textDecoration: visible ? undefined : "line-through",
                  }}
                >
                  {formatType(t)} ({typeCounts[t]})
                </Tag.CheckableTag>
              );
            })}
            {hiddenTypes.length > 0 && (
              <Button type="link" size="small" onClick={() => setHiddenTypes([])}>
                Show all
              </Button>
            )}
          </Space>
          {hiddenCount > 0 && (
            <div style={{ color: "#888", fontSize: 12, marginTop: 6 }}>
              {hiddenCount} {hiddenPresent.map((t) => formatType(t).toLowerCase()).join(", ")}{" "}
              exception{hiddenCount !== 1 ? "s" : ""} hidden.{" "}
              <Button
                type="link"
                size="small"
                style={{ padding: 0, height: "auto", fontSize: 12 }}
                onClick={() => setHiddenTypes(hiddenTypes.filter((t) => !presentTypes.includes(t)))}
              >
                Show
              </Button>
            </div>
          )}
        </div>
      )}
      {isLoading ? (
        <Spin size="large" className="mt-10 mx-auto block" />
      ) : (
        <Table
          dataSource={jobGroups}
          columns={columns}
          rowKey="jobId"
          size="small"
          pagination={{ pageSize: 20, current: page, onChange: setPage, showSizeChanger: false }}
          onRow={(record) => ({
            onClick: () => setSelectedJobId(record.jobId),
            style: { cursor: "pointer" },
          })}
        />
      )}

      <Modal
        title={`Split Job: ${splitTarget?.job_order_name || ""}`}
        open={splitModalOpen}
        onOk={handleSplit}
        onCancel={() => setSplitModalOpen(false)}
        confirmLoading={isSplitting}
        okText="Split"
      >
        <p style={{ marginBottom: 16 }}>
          Split <strong>{splitTarget?.job_order_name}</strong> ({splitTarget?.job_part_name}) into
          smaller jobs. Quantities and durations will be divided evenly. The original job will be
          removed and replaced by the new split jobs.
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span>Split into:</span>
          <InputNumber
            min={2}
            max={20}
            value={splitCount}
            onChange={(v) => setSplitCount(v || 2)}
          />
          <span>jobs</span>
        </div>
        {splitTarget && (
          <div style={{ marginTop: 16 }}>
            <Space size={[4, 4]} wrap>
              {splitTarget.allTypes.map((t) => (
                <Tag key={t} color={TYPE_COLORS[t] || "default"}>
                  {formatType(t)}
                </Tag>
              ))}
            </Space>
            <p style={{ marginTop: 8, color: "#888" }}>
              {splitTarget.allTotal} exception{splitTarget.allTotal !== 1 ? "s" : ""} on this job will be
              cleared after splitting.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
}
